import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal } from "lucide-react";
import EventCard from "@/components/EventCard";
import { fetchEvents, fetchTaxonomy } from "@/lib/api";

export default function EventsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialCategory = searchParams.get("category") || "All";
  const initialCity = searchParams.get("city") || "All";

  const [events, setEvents] = useState([]);
  const [taxonomy, setTaxonomy] = useState({ categories: [], cities: [] });
  const [category, setCategory] = useState(initialCategory);
  const [city, setCity] = useState(initialCity);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTaxonomy().then(setTaxonomy).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchEvents({
      category: category === "All" ? undefined : category,
      city: city === "All" ? undefined : city,
      q: q || undefined,
    })
      .then(setEvents)
      .finally(() => setLoading(false));

    const params = {};
    if (category && category !== "All") params.category = category;
    if (city && city !== "All") params.city = city;
    setSearchParams(params, { replace: true });
  }, [category, city, q, setSearchParams]);

  const categories = useMemo(() => ["All", ...taxonomy.categories], [taxonomy]);
  const cities = useMemo(() => ["All", ...taxonomy.cities], [taxonomy]);

  return (
    <div data-testid="events-page" className="max-w-7xl mx-auto px-6 md:px-12 pt-16 pb-24">
      {/* Header */}
      <div className="mb-12 grid grid-cols-1 md:grid-cols-12 gap-6 items-end">
        <div className="md:col-span-7">
          <p className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-3">◆ Browse Live</p>
          <h1 className="font-display text-5xl md:text-7xl uppercase tracking-tighter leading-[0.92]">
            What's<br />
            <span className="text-[#B4FF39]">happening</span> tonight.
          </h1>
        </div>
        <div className="md:col-span-5">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="search-input"
              placeholder="Search by title, venue, city…"
              className="w-full bg-white/5 border border-white/10 text-white pl-11 pr-4 py-3.5 focus:border-[#B4FF39] outline-none placeholder:text-zinc-500 font-mono-tight text-sm"
            />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="border-t border-b border-white/5 py-5 mb-10 flex flex-col gap-5">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-zinc-500 mr-2">
            <SlidersHorizontal className="w-3 h-3" /> Category
          </span>
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setCategory(c)}
              data-testid={`filter-category-${c.replace(/\s+/g, "-").toLowerCase()}`}
              className={`px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] font-bold border transition-colors ${
                category === c
                  ? "bg-[#B4FF39] border-[#B4FF39] text-[#0A0A0A]"
                  : "border-white/10 text-zinc-300 hover:border-white/30"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mr-2">◆ City</span>
          {cities.map((c) => (
            <button
              key={c}
              onClick={() => setCity(c)}
              data-testid={`filter-city-${c.replace(/\s+/g, "-").toLowerCase()}`}
              className={`px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] font-bold border transition-colors ${
                city === c
                  ? "bg-white text-[#0A0A0A] border-white"
                  : "border-white/10 text-zinc-300 hover:border-white/30"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      {/* Results */}
      <div className="flex items-center justify-between mb-6">
        <p className="text-xs tracking-[0.2em] uppercase text-zinc-500 font-mono-tight" data-testid="results-count">
          {loading ? "Loading…" : `${events.length} event${events.length === 1 ? "" : "s"} found`}
        </p>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="border border-white/10 bg-white/[0.02]">
              <div className="aspect-[4/3] bg-white/[0.04] animate-pulse" />
              <div className="p-6 space-y-3">
                <div className="h-6 bg-white/[0.04] animate-pulse" />
                <div className="h-3 w-1/2 bg-white/[0.04] animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      ) : events.length === 0 ? (
        <div data-testid="empty-state" className="border border-white/10 p-16 text-center">
          <p className="font-display text-3xl mb-3 uppercase tracking-tight">Nothing live yet.</p>
          <p className="text-zinc-400 text-sm">Try widening your filters — or check back tonight.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {events.map((e, i) => (
            <EventCard key={e.id} event={e} index={i} />
          ))}
        </div>
      )}
    </div>
  );
}
